/* globals window */
import * as React from 'react';
import {defaults, noop} from 'lodash';
import {emptyArray, emptyObject} from '@packages/utilities/fp/utils';
import Balloon, {type Props as BalloonProps} from './Balloon';
import {styled} from '@packages/utilities/styled';
import {place, positions, centerOffset, pad} from '@packages/utilities/box';
import {unselectable} from './constants/Styles';
import {useSpring, animated} from '@react-spring/web';
import {chainFunctions} from '@packages/utilities/component';
import {
  FocusTrap,
  Slot,
  useRegisterLegacyOverlay,
} from '@packages/systems/spring';
import {createId} from './utils/useUniqueId';

import type {PlacementIdentifier, Box} from '@packages/utilities/box';

import Mask from './Mask';
import {
  Layer,
  Locator,
  useSpringModalViewport,
} from '@packages/systems/ui/overlay';
import Keyboard from './Keyboard';
import {ENTER, ESC} from '@packages/utilities/key-utils';
import {FloatingNode} from '@floating-ui/react';

const noDeps: Array<never> = [];

export type PopoverState = {
  /** True if the popover is visible. */
  open: boolean;
};

type AnchorProps =
  | {
      asChild: false;
      /**
       * The item to anchor the popover to.
       *
       * Must only be a single element.
       */ children: (ref: React.RefObject<HTMLElement>) => React.ReactNode;
    }
  | {
      asChild: true;
      children: React.ReactNode;
    };

export type RenderChildrenProps = PopoverState & {
  /**
   * Call this to open the popover.
   *
   * Note: This will not affect internal state if you
   * provide a `state` prop, but *will* cause `onChange`
   * to fire so you can update your state.
   *
   * > See also: Props: `state`, `defaultState`, `onChange`
   */
  openPopover: (arg1: unknown) => void;
  /**
   * A wrapper component you can use to signify the
   * part of the UI the popover should be attached to.
   *
   * Note: Unless you want to have duplicate popovers, you should
   * only use a single `<Anchor>` per Popover.
   */
  Anchor: React.ComponentType<AnchorProps>;
  getAnchorProps: () => {
    ['aria-controls']: string;
    ['aria-expanded']: boolean;
    ['aria-haspopup']: string;
    id: string;
  };
};

type MaskConfig = {
  /** ID used to key each mask */
  id: string;
  /** Determines whether to show a transparent overlay for the mask */
  isMask?: boolean;
  /** Determines the overlay color for the mask */
  bright?: boolean;
  /** Whether the anchor should render under mask or not */
  transparentAnchor?: boolean;
  /** Determines which part or "cutout" of the mask should be transparent */
  cutouts?: Array<Box>;
  /** Should the anchor element be interactive? */
  allowMouseEventsInWindow?: boolean;
  /** Optional styles for the Mask svg element */
  style?: React.CSSProperties;
  /** Optional viewport to render the mask over */
  viewport?: Box;
  /** Optional anchor to render the mask around */
  anchor?: Box;
  // untyped properties discovered when making this type exact
  animate?: AnimatePopoverConfig;
  padded?: boolean;
  onClick?: () => void;
};

export type MasksConfig = MaskConfig[];
export type MasksConfigFunction = (viewport: Box, anchor: Box) => MasksConfig;

export type RenderPopoverContentType = {
  /**
   * Call this to close the popover.
   *
   * Note: This will not affect internal state if you
   * provide a `state` prop, but *will* cause `onChange`
   * to fire so you can update your state.
   *
   * > See also: Props: `state`, `defaultState`, `onChange`
   */
  closePopover: any;
  /**
   * The box describing the position of the popover.
   */
  rect: Box | null | undefined;
  /**
   * The box describing the position of the anchor this popover is
   * attached to.
   */
  anchor: Box;
  /**
   * The box describing the position of the viewport
   * the popover will be rendered inside.
   */
  viewport: Box;
  /**
   * A string representing how the popover was positioned relative
   * to its anchor.
   */
  position: PlacementIdentifier;
};

export type RenderPopoverContentFnType = (
  arg1: RenderPopoverContentType
) => React.ReactElement | null;

export type PopoverProps = {
  id?: string;
  /**
   * A function to render the internal contents of the popover.
   */
  renderPopoverContent: RenderPopoverContentFnType;
  /**
   * A string representing how you'd prefer to position the popover
   * relative to its anchor.
   *
   * See also: `renderPopoverContent`
   */
  placement?: PlacementIdentifier;
  /**
   * A function called to render any contents relating to this
   * popover.
   */
  children: (arg1: RenderChildrenProps) => React.ReactElement<any>;
  /**
   * If provided, this component's initial state will be
   * this value. After that, the state will be maintained
   * internally.
   *
   * This prop, in combination with `state`, behaves
   * similarly to the `value`/`defaultValue`/`onChange` props of React's
   * built-in `<input>` component.
   *
   * > See also: `state`, `onChange`
   */
  defaultState?: PopoverState | null | undefined;
  /**
   * If provided, this component will not use any internal
   * state, and instead allow you to control it directly.
   *
   * This prop, in combination with `defaultState`, behaves
   * similarly to the `value`/`defaultValue`/`onChange` props of React's
   * built-in `<input>` component.
   *
   * > See also: `defaultState`, `onChange`
   */
  state?: PopoverState | null | undefined;
  /**
   * Whenever an internal action would cause the event to change,
   * this handler will fire with the new state as its first argument.
   *
   * This, in combination with `state` and `defaultState`, behaves
   * similar to the `value`/`defaultValue`/`onChange` props of
   * React's built-in `<input>` component.
   *
   * > See also: `state`, `defaultState`
   */
  onChange?: (arg1: PopoverState) => void;
  /**
   * Whether or not to display an arrow/"caret" pointing to the center of
   * the anchor.
   *
   * Defaults to `true`.
   */
  showArrow?: boolean;
  /**
   * The mask is the overlay between the popover and UI under the popover. It
   * is undefined by default — showing the semi-opaque color — and has the
   * following options:
   *
   * 'NONE':        renders no mask, which means that _all pointer events will
   *                pass through_ since there is no mask. This is typically
   *                used for nested popovers and the interactions between them.
   *
   * 'TRANSPARENT': renders a transparent mask, typically used to show UI content
   *                underneath the popover while preventing pointer events on those
   *                UI. Clicking on the mask still triggers the close event.
   *
   * 'FULL':        renders an overlay; UI underneath are _not_ interactive
   *
   * 'BRIGHT':      renders a translucent mask that's white-ish
   *
   * MasksConfig:   renders one or more custom masks. An example usage is to
   *                enable interactivity with elements under the UI for an
   *                onboarding tutorial experience.
   *                Can also accept a function that returns `MasksConfig`, which
   *                passes in the current anchor and viewport dimensions
   */
  showMask?:
    | boolean
    | 'NONE'
    | 'TRANSPARENT'
    | 'BRIGHT'
    | 'FULL'
    | MasksConfig
    | MasksConfigFunction;
  /**
   * Whether or not the mask has a padding on the anchored element.
   */
  paddedMask?: boolean;
  /**
   * Different theme. If true the popover will have a darker color
   * and slightly different box shadow and border radius.
   */
  dark?: boolean;
  /**
   * Different theme. If true the popover will have a blue color and a lighter
   * box shadow.
   */
  accent?: boolean;
  /**
   * Different theme. If true the popover will have the dropdown color
   */
  dropdown?: boolean;
  /**
   * A function to allow Enter/Return to close the popover
   */
  onEnterKeyPress?: () => void;
  /**
   * A function to apply an offset on the popover
   */
  applyOffset?: ApplyOffset;
  /**
   * A config object to enable animating. Animation is currently
   * from 0 opacity and from the direction _opposite_ of the popover placement
   */
  animate?: AnimatePopoverConfig;
  /** Border width for the balloon / wrapping component  */
  borderWidth?: number;
  /** Border color for the balloon / wrapping component  */
  borderColor?: string;
  /** Key that React uses to determine where items in a new list have moved.  */
  key?: string;
  /** Should the popover trap focus? Defaults to true. */
  focusTrap?: boolean;
  /** Helps us report metrics on the usage of popovers */
  metricsIdentifier?: string;
  /** Optional style for popover balloon. */
  balloonStyle?: React.CSSProperties;
  /** Choose whether to force placement of chevron **/
  forcePlacement?: boolean;
};

export type NamedPopoverProps = PopoverProps & {
  name: ValidPopoverName;
};

export type ValidPopoverName =
  | 'propertyList-textShadows'
  | 'propertyList-backgrounds'
  | 'propertyList-boxShadows'
  | 'propertyList-transforms'
  | 'propertyList-transitions'
  | 'propertyList-filters'
  | 'textColumns'
  | 'textShadow'
  | 'easing'
  | 'color'
  | 'border-color'
  | 'outline-color'
  | 'background-color'
  | 'transformSettings'
  | 'textColumnsSettings'
  | 'textWrappingSettings'
  | 'textStrokeColor'
  | 'fontVariationSettings'
  | 'textDecorationColor';

type PopoverKeyboardListenerProps = {
  onEscape: () => void;
  onEnterKeyPress: () => void;
  children: React.ReactElement<any>;
};

class PopoverKeyboardListener extends React.PureComponent<PopoverKeyboardListenerProps> {
  onEvent = (e: KeyboardEvent) => {
    if (e.keyCode === ESC) {
      this.props.onEscape();
      return false;
    }
    if (e.keyCode === ENTER && this.props.onEnterKeyPress) {
      this.props.onEnterKeyPress();
      return false;
    }
  };

  render() {
    return <Keyboard onEvent={this.onEvent}>{this.props.children}</Keyboard>;
  }
}

// TODO: These should (probably) be part of our theme:
const arrowSize = 8;
const padAnchor = pad(arrowSize - 3);

export type AnimatePopoverConfig = {
  /**
   * The amount of time in milliseconds to delay before animating
   */
  delay?: number;
  /**
   * Should the popover animate into position (towards the anchor)?
   */
  translate?: boolean;
};

type PopoverBodyProps = {
  anchor?: Box;
  closePopover: (event?: React.SyntheticEvent<any>) => void;
  onWheel: React.WheelEventHandler<SVGSVGElement>;
  renderPopoverContent: PopoverProps['renderPopoverContent'];
  placement: PopoverProps['placement'];
  applyOffset: PopoverProps['applyOffset'];
  forcePlacement: PopoverProps['forcePlacement'];
  onEnterKeyPress?: () => void;
  animate?: AnimatePopoverConfig;
  showArrow: PopoverProps['showArrow'];
  dark: PopoverProps['dark'];
  accent: PopoverProps['accent'];
  dropdown: PopoverProps['dropdown'];
  borderWidth: PopoverProps['borderWidth'];
  borderColor: PopoverProps['borderColor'];
  contentId: string;
  focusTrap?: PopoverProps['focusTrap'];
  balloonStyle: PopoverProps['balloonStyle'];
};

const PopoverBody = React.memo(
  ({
    anchor,
    closePopover,
    onWheel,
    renderPopoverContent,
    placement,
    applyOffset,
    forcePlacement,
    onEnterKeyPress = noop,
    animate,
    showArrow = true,
    dark = false,
    accent = false,
    dropdown = false,
    borderWidth = 0,
    borderColor,
    contentId,
    focusTrap = true,
    balloonStyle,
  }: PopoverBodyProps) => {
    const viewport = useSpringModalViewport();
    if (!anchor) {
      return null;
    }

    const paddedAnchor = padAnchor(anchor);

    return (
      <PopoverKeyboardListener
        onEscape={closePopover}
        onEnterKeyPress={onEnterKeyPress}
      >
        <Locator>
          {(targetRef, target) => (
            <LockedPopoverPositioner
              onWheel={onWheel}
              placement={placement}
              viewport={viewport}
              anchor={paddedAnchor}
              rect={target}
              applyOffset={applyOffset}
              forcePlacement={forcePlacement}
            >
              {({rect, position}) => {
                const popoverContent = renderPopoverContent({
                  closePopover,
                  viewport,
                  anchor,
                  rect,
                  position,
                });

                return (
                  <Animate
                    type="popover"
                    animate={animate}
                    placement={placement}
                  >
                    <PopoverBalloon
                      arrowSize={showArrow ? arrowSize : 0}
                      anchor={anchor}
                      rect={rect}
                      position={position}
                      borderWidth={borderWidth}
                      borderColor={borderColor}
                      dark={dark}
                      accent={accent}
                      dropdown={dropdown}
                      // @ts-expect-error — TS2322: Type CSSProperties | undefined is not assignable to type (StyleObject & CSSProperties) | undefine
                      style={balloonStyle}
                    >
                      {/**
                       * We are wrapping our popover content in a div to measure it.
                       * We are setting its display to `inline-block` so that its children's margins are not collapsed.
                       * If we didn't set this `inline-block`, then any popoverContent with vertical margins won't get measured correctly.
                       * @see https://webflow.slack.com/archives/C04MMAVRZDE/p1772750592991489
                       *
                       * This is necessary so that the PopoverBalloon will be the correct shape for the Popover's contents.
                       */}
                      {focusTrap ? (
                        <FocusTrap
                          role="dialog"
                          id={contentId}
                          shouldTrapFocus
                          tabIndex={-1}
                        >
                          <div
                            ref={targetRef}
                            style={{display: 'inline-block'}}
                          >
                            {popoverContent}
                          </div>
                        </FocusTrap>
                      ) : (
                        <div ref={targetRef} style={{display: 'inline-block'}}>
                          {popoverContent}
                        </div>
                      )}
                    </PopoverBalloon>
                  </Animate>
                );
              }}
            </LockedPopoverPositioner>
          )}
        </Locator>
      </PopoverKeyboardListener>
    );
  }
);

interface PopoverContentProps {
  nodeId: string | undefined;
  targetRef: React.RefObject<HTMLElement>;
  contentId: string;
  closePopover: (event?: React.SyntheticEvent<any>) => void;
  onWheel: React.WheelEventHandler<SVGSVGElement>;
  paddedMask: boolean;
  masksConfig: MasksConfig | MasksConfigFunction;
  metricsIdentifier?: string;
  focusTrap: PopoverProps['focusTrap'];
  balloonStyle: PopoverProps['balloonStyle'];
  placement: PopoverProps['placement'];
  applyOffset: PopoverProps['applyOffset'];
  forcePlacement: PopoverProps['forcePlacement'];
  onEnterKeyPress?: () => void;
  animate: PopoverProps['animate'];
  showArrow: PopoverProps['showArrow'];
  dark: PopoverProps['dark'];
  accent: PopoverProps['accent'];
  dropdown: PopoverProps['dropdown'];
  borderWidth: PopoverProps['borderWidth'];
  borderColor: PopoverProps['borderColor'];
  renderPopoverContent: PopoverProps['renderPopoverContent'];
}

const getTranslateDirection = (
  placement: undefined | string | PlacementIdentifier
) => {
  switch (placement) {
    case 'top':
      return 'translate3d(0, -50px, 0)';
    case 'left':
      return 'translate3d(-50px, 0, 0)';
    case 'right':
      return 'translate3d(50px, 0, 0)';
    case 'bottom':
    default:
      return 'translate3d(0, 50px, 0)';
  }
};

const Animate = React.memo<{
  animate?: AnimatePopoverConfig;
  type: 'popover' | 'mask';
  placement?: PlacementIdentifier;
  children: React.ReactNode;
}>(function Animate({animate, type, placement, children}) {
  const shouldTranslate = animate && animate.translate && type === 'popover';
  const delay = (animate && animate.delay) || 0;

  const style = useSpring(
    animate
      ? {
          to: {
            opacity: 1,
            transform: shouldTranslate ? 'translate3d(0,0,0)' : undefined,
          },
          from: {
            opacity: 0,
            transform: shouldTranslate
              ? getTranslateDirection(placement)
              : undefined,
          },
          delay,
        }
      : emptyObject
  );

  if (animate) {
    return <animated.div style={style}>{children}</animated.div>;
  }

  return children;
});

/**
 * Generate a function the Mask based on a mask-click
 * handler (which in our case will probably close the Popover).
 *
 * We use `weakMemo` here to avoid excessive function allocations
 * in each render.
 */
const MaskRenderer = React.memo(
  ({
    anchor,
    closePopover,
    onWheel,
    paddedMask,
    masksConfig,
    metricsIdentifier = 'popover-mask',
  }: {
    anchor?: Box;
    closePopover: (event?: React.SyntheticEvent<any>) => void;
    onWheel: React.WheelEventHandler<SVGSVGElement>;
    paddedMask: boolean;
    masksConfig: MasksConfig | MasksConfigFunction;
    metricsIdentifier?: string;
  }) => {
    const viewport = useSpringModalViewport();
    if (!anchor) {
      return null;
    }

    if (typeof masksConfig === 'function') {
      masksConfig = masksConfig(viewport, anchor);
    }
    return masksConfig.length === 0
      ? null
      : masksConfig.map(
          ({
            id,
            padded = paddedMask,
            isMask = true,
            bright = false,
            cutouts = emptyArray,
            transparentAnchor = true,
            allowMouseEventsInWindow = false,
            style,
            viewport: maskViewport = viewport,
            anchor: maskAnchor = anchor,
            animate,
            onClick,
          }) => {
            return (
              <Animate type="mask" key={id} animate={animate}>
                <Mask
                  data-dd-action-name={metricsIdentifier}
                  onClick={
                    onClick
                      ? chainFunctions(onClick, closePopover)
                      : closePopover
                  }
                  onContextMenu={(event) => {
                    event.preventDefault();
                    if (
                      event.clientX >= anchor.left &&
                      event.clientX <= anchor.left + anchor.width &&
                      event.clientY >= anchor.top &&
                      event.clientY <= anchor.top + anchor.height
                    ) {
                      return;
                    }
                    return closePopover();
                  }}
                  onWheel={onWheel}
                  viewport={maskViewport}
                  anchor={maskAnchor}
                  padded={padded}
                  isMask={isMask}
                  bright={bright}
                  transparentAnchor={transparentAnchor}
                  allowMouseEventsInWindow={allowMouseEventsInWindow}
                  cutouts={cutouts}
                  style={style}
                />
              </Animate>
            );
          }
        );
  }
);

const PopoverContent = React.memo(
  ({
    nodeId,
    targetRef,
    contentId,
    closePopover,
    onWheel,
    paddedMask,
    masksConfig,
    metricsIdentifier,
    focusTrap,
    balloonStyle,
    placement,
    applyOffset,
    forcePlacement,
    onEnterKeyPress = noop,
    animate,
    showArrow,
    dark,
    accent,
    dropdown,
    borderWidth,
    borderColor,
    renderPopoverContent,
  }: PopoverContentProps) => {
    return (
      <FloatingNode id={nodeId}>
        <Locator targetRef={targetRef}>
          {(_unusedRef, anchor) => (
            <React.Fragment key={contentId}>
              {/**
               * The Mask also wraps the Anchor so we can "cut" the window into it.
               *
               * We could potentially place the Mask and the Popover on the same Layer,
               * but I'm not sure exactly how Mask is attaching its event listeners and whatnot.
               *
               * So, I am going to continue the double-layer approach of LegacyPopover for now.
               */}
              <Layer>
                <MaskRenderer
                  anchor={anchor}
                  closePopover={closePopover}
                  onWheel={onWheel}
                  paddedMask={Boolean(paddedMask)}
                  masksConfig={masksConfig}
                  metricsIdentifier={metricsIdentifier}
                />
              </Layer>
              <Layer>
                <PopoverBody
                  anchor={anchor}
                  closePopover={closePopover}
                  onWheel={onWheel}
                  contentId={contentId}
                  focusTrap={focusTrap}
                  balloonStyle={balloonStyle}
                  placement={placement}
                  applyOffset={applyOffset}
                  forcePlacement={forcePlacement}
                  onEnterKeyPress={onEnterKeyPress}
                  animate={animate}
                  showArrow={showArrow}
                  dark={dark}
                  accent={accent}
                  dropdown={dropdown}
                  borderWidth={borderWidth}
                  borderColor={borderColor}
                  renderPopoverContent={renderPopoverContent}
                />
              </Layer>
            </React.Fragment>
          )}
        </Locator>
      </FloatingNode>
    );
  }
);

export const OPENED_STATE = {open: true} as const;
export const CLOSED_STATE = {open: false} as const;

/**
 * A special hook to create an provide a ref (an HTMLDivElement) to act as an
 * anchor to help the FocusReturn mechanism take a best guess for targeting
 * the tabbable element that would render as part of `<Anchor />`.
 *
 * With how `Popover` and `Anchor` rendering works, it's not consistent that
 * it will always render a tabbable element. Sometimes there's more than 1...
 * Sometimes there's zero.
 *
 * This will be improved overtime. But for now, this trick works as a stop-gap.
 */
const usePopoverFocusReturn = ({
  id,
  focusTrap = true,
}: {
  id?: string;
  focusTrap?: boolean;
}) => {
  const focusReturnRef = React.useRef<HTMLDivElement | null>(null);

  /**
   * Disabling focus return in E2E tests because it's causing issues with
   * Cypress' focus handling.
   *
   * 2023-03-03
   */
  let isE2ETestEnv = process.env.NODE_ENV === 'test' || process.env.CYPRESS;
  if (typeof window !== 'undefined') {
    if (window.Cypress) {
      isE2ETestEnv = true;
    }
  }

  const focusReturn = React.useCallback(() => {
    if (focusReturnRef.current) {
      const targetNode = focusReturnRef.current.previousElementSibling;
      if (!targetNode) return;
      // @ts-expect-error - TS2339 - Property 'focus' does not exist on type 'Element'.
      if (targetNode.focus) {
        // @ts-expect-error - TS2339 - Property 'focus' does not exist on type 'Element'.
        targetNode.focus();
      }
    }
  }, []);

  if (!focusTrap) return {ref: null, focusReturn};

  let ref = (
    <div
      data-focus-return={id}
      style={{display: 'none'}}
      ref={focusReturnRef}
    />
  );

  if (isE2ETestEnv) {
    // @ts-expect-error - TS2322 - Type 'null' is not assignable to type 'Element'.
    ref = null;
  }

  return {ref, focusReturn};
};

/**
 *
 * A bridge component to migrate legacy Popovers off of findDomNode APIs so we can upgrade to React 19.
 *
 * @deprecated use `Popover` from `@packages/systems/spring` instead
 */
export class DeprecatedPopoverBridge extends React.Component<
  PopoverProps,
  PopoverState
> {
  state: PopoverState =
    this.props.defaultState != null ? this.props.defaultState : CLOSED_STATE;

  baseId = this.props.id || `popover-${createId(DeprecatedPopoverBridge)}`;

  isControlled() {
    return this.props.state != null;
  }

  handleStateChange(state: PopoverState) {
    if (this.props.onChange) {
      this.props.onChange(state);
    }

    if (!this.isControlled()) {
      this.setState(state);
    }
  }

  getState(): PopoverState {
    return this.props.state != null ? this.props.state : this.state;
  }

  openPopover = () => {
    // It's possible for the `openPopover` event to bubble up to the mask.
    //
    // You'd *think* this would be a no-op if the popover is already open, but
    // it's possible for the `openPopover` event to come in *after* the event
    // that should *close* the popover.
    if (this.getState().open) {
      return;
    }
    this.handleStateChange(OPENED_STATE);
  };

  getAnchorProps = () => {
    const {open} = this.getState();
    const triggerId = `${this.baseId}-root`;
    const contentId = `${this.baseId}-content`;

    return {
      id: triggerId,
      'aria-haspopup': 'dialog',
      'aria-expanded': Boolean(open),
      'aria-controls': contentId,
    };
  };

  Anchor: React.ComponentType<AnchorProps> = (() => {
    const Anchor = ({children, asChild}: AnchorProps) => {
      const targetRef = React.useRef<HTMLElement | null>(null);
      const {open} = this.getState();
      const anchorProps = this.getAnchorProps();
      const {focusTrap} = this.props;
      const {nodeId} = useRegisterLegacyOverlay({isOpen: open});

      const {ref: focusReturnRef, focusReturn} = usePopoverFocusReturn({
        id: anchorProps.id,
        focusTrap,
      });
      const contentId = anchorProps['aria-controls'];

      const onWheel = React.useCallback<React.WheelEventHandler<SVGSVGElement>>(
        (event) => {
          event.preventDefault();
          event.stopPropagation();
        },
        []
      );
      const closePopover = React.useCallback(
        (event?: React.SyntheticEvent<any>): void => {
          // createPortal propagates events up the component tree which in a
          // controlled state would reopen this
          if (event && this.isControlled()) {
            event.stopPropagation();
          }
          this.handleStateChange(CLOSED_STATE);

          /**
           * Attempts to handle a focus return to the Popover trigger.
           */
          focusReturn();
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        noDeps
      );

      const {metricsIdentifier, paddedMask, showMask, balloonStyle} =
        this.props;

      const defaultMask = React.useMemo(
        () => [
          {
            id: 'popover-mask',
            padded: Boolean(paddedMask),
            isMask: showMask !== 'TRANSPARENT',
            bright: showMask === 'BRIGHT',
          },
        ],
        [showMask, paddedMask]
      );

      const masksConfig =
        showMask === 'NONE'
          ? emptyArray
          : Array.isArray(showMask) || typeof showMask === 'function'
            ? showMask
            : defaultMask;

      const {
        placement,
        applyOffset,
        forcePlacement,
        onEnterKeyPress,
        animate,
        showArrow,
        dark,
        accent,
        dropdown,
        borderWidth,
        borderColor,
        renderPopoverContent,
      } = this.props;

      return (
        <>
          {asChild ? (
            <Slot ref={targetRef}>{children}</Slot>
          ) : (
            children(targetRef)
          )}
          {focusReturnRef}
          {open ? (
            <PopoverContent
              nodeId={nodeId}
              targetRef={targetRef}
              contentId={contentId}
              closePopover={closePopover}
              onWheel={onWheel}
              paddedMask={Boolean(paddedMask)}
              masksConfig={masksConfig}
              metricsIdentifier={metricsIdentifier}
              focusTrap={focusTrap}
              balloonStyle={balloonStyle}
              placement={placement}
              applyOffset={applyOffset}
              forcePlacement={forcePlacement}
              onEnterKeyPress={onEnterKeyPress}
              animate={animate}
              showArrow={showArrow}
              dark={dark}
              accent={accent}
              dropdown={dropdown}
              borderWidth={borderWidth}
              borderColor={borderColor}
              renderPopoverContent={renderPopoverContent}
            />
          ) : null}
        </>
      );
    };

    Anchor.displayName = 'Anchor';

    return Anchor;
  })();

  render() {
    return this.props.children({
      open: this.getState().open,
      openPopover: this.openPopover,
      Anchor: this.Anchor,
      getAnchorProps: this.getAnchorProps,
    });
  }
}

const wrapperStyle = {
  position: 'absolute',
  top: 0,
  left: 0,
  ...unselectable,
} as const;

function defaultApplyOffset(popoverBox: Box) {
  return {top: popoverBox.top, left: popoverBox.left};
}

export type ApplyOffset = (arg1: Box) => {
  top: number;
  left: number;
};

type PopoverPositionerProps = {
  viewport: Box;
  anchor: Box;
  rect?: Box;
  children: (arg1: {
    position: PlacementIdentifier;
    rect: Box | null | undefined;
  }) => React.ReactElement;
  style?: React.CSSProperties;
  placement?: PlacementIdentifier;
  forcePlacement?: boolean;
  onWheel?: React.SVGProps<SVGSVGElement>['onWheel'] | null | undefined;
  applyOffset?: ApplyOffset;
  animate?: AnimatePopoverConfig;
};

/**
 * @deprecated use `Popover` from `@packages/systems/spring`
 */
export const PopoverPositioner: React.ComponentType<PopoverPositionerProps> = ({
  viewport,
  anchor,
  rect,
  children,
  onWheel,
  style,
  placement = 'bottom',
  applyOffset = defaultApplyOffset,
}: PopoverPositionerProps) => {
  const findPlacement = place(placement);
  const popoverPlacement =
    rect && anchor ? findPlacement(viewport, anchor, rect) : placement;

  return popoverRender({
    viewport,
    anchor,
    rect,
    children,
    onWheel,
    style,
    placement: popoverPlacement,
    applyOffset,
  });
};

const popoverRender = ({
  viewport,
  anchor,
  rect,
  children,
  onWheel,
  style,
  placement,
  applyOffset,
}: {
  viewport: Box;
  anchor: undefined | Box;
  rect: undefined | Box;
  children: (arg1: {
    position: PlacementIdentifier;
    rect: Box | null | undefined;
  }) => React.ReactElement;
  style?: React.CSSProperties;
  placement: PlacementIdentifier;
  onWheel?: React.SVGProps<SVGSVGElement>['onWheel'] | null | undefined;
  applyOffset: ApplyOffset;
}) => {
  if (!rect || !anchor) {
    return (
      <div style={wrapperStyle}>
        {children({
          position: placement,
          rect: null,
        })}
      </div>
    );
  }
  const popoverBox = positions[placement](viewport, anchor, rect);

  const child = children({
    position: placement,
    rect: popoverBox,
  });

  return (
    <div
      style={defaults(
        {
          ...applyOffset(popoverBox),
          ...style,
        },
        wrapperStyle
      )}
      // @ts-expect-error - TS2322 - Type '((arg1: WheelEvent) => void | null | undefined) | undefined' is not assignable to type 'WheelEventHandler<HTMLDivElement> | undefined'.
      onWheel={onWheel}
    >
      {child}
    </div>
  );
};

/**
 * @deprecated use `Popover` from `@packages/systems/spring` instead
 */
export const LockedPopoverPositioner: React.ComponentType<
  PopoverPositionerProps
> = ({
  viewport,
  anchor,
  rect,
  children,
  onWheel,
  style,
  placement = 'bottom',
  forcePlacement = false,
  applyOffset = defaultApplyOffset,
}: PopoverPositionerProps) => {
  const [position, setPosition] = React.useState<null | PlacementIdentifier>(
    null
  );

  React.useLayoutEffect(() => {
    if (!position && anchor && rect) {
      const findPlacement = place(placement);
      const popoverPlacement = forcePlacement
        ? placement
        : findPlacement(viewport, anchor, rect);
      setPosition(popoverPlacement);
    }
  }, [position, placement, viewport, anchor, rect, forcePlacement]);

  return popoverRender({
    viewport,
    anchor,
    rect,
    children,
    onWheel,
    style,
    placement: position || placement,
    applyOffset,
  });
};

const borderRadius = 3;

type PopoverBalloonProps = BalloonProps & {
  anchor: Box | null | undefined;
  id?: string;
  rect: Box | null | undefined;
  position: PlacementIdentifier;
  children: React.ReactNode;
  dark?: boolean;
  accent?: boolean;
  dropdown?: boolean;
  style?: React.CSSProperties;
};

/**
 * @deprecated use `Popover` from `@packages/systems/spring` instead
 */
export const PopoverBalloon: React.ComponentType<PopoverBalloonProps> = styled(
  ({anchor, rect, position, children, ...rest} /*: PopoverBalloonProps */) => [
    anchor && rect && (
      <Balloon
        key="balloon"
        position={position}
        // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'any' can't be used to index type '{ readonly top: (anchor: Box, popover: Box) => number; readonly bottom: (anchor: Box, popover: Box) => number; readonly left: (anchor: Box, popover: Box) => number; readonly right: (anchor: Box, popover: Box) => number; readonly center: (_a: Box, _b: Box) => number; }'.
        arrowOffset={centerOffset[position](anchor, rect)}
        dropShadow={!rest.accent}
        canvasShadow={rest.accent}
        {...rest}
      />
    ),
    <div
      key="child"
      style={{position: 'relative', overflow: 'hidden', borderRadius}}
      data-legacy-ds="Popover"
    >
      {children}
    </div>,
  ]
)(({theme, rect, dark, accent, dropdown, style}) => ({
  position: 'absolute',
  borderRadius,
  color: dark
    ? theme.popover.backgroundColorDark
    : accent
      ? theme.colors.accentBright
      : dropdown
        ? theme.dropdown.backgroundColor
        : theme.popover.backgroundColor,
  ...(rect && {
    width: rect.width,
    height: rect.height,
    ...style,
  }),
}));
